/**
 * Warm caches before first interaction: SFX, and the pictures canvas draw sites read during
 * play (Fall objects, catcher flask, merge tiers).
 *
 * Pictures are drawn in code, so "loading" one means drawing it (and, for a picture the DOM
 * shows too, PNG-encoding it): resolving its URL does that and registers the canvas in the
 * image cache. Nothing happens at import time; the work runs while the loader is up, one
 * picture per task so the page stays responsive, and is done before the first Fall frame.
 */
import * as C from "../config/constants.js";
import { mergeTierImageUrl } from "./merge-tier-urls.js";
import { GAMEPLAY_STATIC_IMAGE_KEYS, gameImageUrl } from "./game-image-urls.js";
import { preloadImageUrl } from "./image-cache.js";

/**
 * Tier configs whose pictures we warm (respects `MAX_MERGE_EVOLUTION_TIERS`).
 */
function evolutionChainForPreload() {
  var chain = C.EVOLUTION_CHAIN || [];
  var cap = C.MAX_MERGE_EVOLUTION_TIERS;
  var n = chain.length;
  if (cap != null) {
    var parsed = Math.floor(Number(cap));
    if (isFinite(parsed)) {
      n = Math.max(1, Math.min(chain.length, parsed));
    }
  }
  return chain.slice(0, n);
}

function cappedMergeAssetIds() {
  var chain = evolutionChainForPreload();
  var seen = {};
  var ids = [];
  for (var i = 0; i < chain.length; i++) {
    var aid = chain[i] && chain[i].assetId;
    if (!aid || seen[aid]) continue;
    seen[aid] = true;
    ids.push(aid);
  }
  return ids;
}

/**
 * Merge tier pictures for evolution dock, pickups, and flask bodies.
 * @returns {string[]}
 */
export function collectMergeImageUrls() {
  return cappedMergeAssetIds().map(mergeTierImageUrl).filter(Boolean);
}

/**
 * Every picture URL warmed before play (resolving them draws the pictures).
 * @returns {string[]}
 */
export function collectAllGameImageUrls() {
  var seen = {};
  var out = [];
  function add(url) {
    if (!url || seen[url]) return;
    seen[url] = true;
    out.push(url);
  }
  GAMEPLAY_STATIC_IMAGE_KEYS.map(gameImageUrl).forEach(add);
  collectMergeImageUrls().forEach(add);
  return out;
}

function nextTask() {
  return new Promise(function (resolve) { setTimeout(resolve, 0); });
}

/**
 * Resolve each picture URL in its own task (drawing it), then wait for any that still load.
 * @returns {Promise<void>}
 */
async function warmGameImages() {
  var jobs = [];
  GAMEPLAY_STATIC_IMAGE_KEYS.forEach(function (key) {
    jobs.push(function () { return gameImageUrl(key); });
  });
  cappedMergeAssetIds().forEach(function (aid) {
    jobs.push(function () { return mergeTierImageUrl(aid); });
  });
  var seen = {};
  var loads = [];
  for (var i = 0; i < jobs.length; i++) {
    if (i > 0) await nextTask();
    var url = jobs[i]();
    if (!url || seen[url]) continue;
    seen[url] = true;
    loads.push(preloadImageUrl(url));
  }
  await Promise.all(loads);
}

/**
 * @param {{ soundManager?: { preloadAll?: () => Promise<unknown> } }} [opts]
 * @returns {Promise<void>}
 */
export async function preloadAllGameAssets(opts) {
  if (typeof window === "undefined") return;

  opts = opts || {};
  var tasks = [];

  if (opts.soundManager && typeof opts.soundManager.preloadAll === "function") {
    tasks.push(opts.soundManager.preloadAll());
  }

  tasks.push(warmGameImages());

  await Promise.all(tasks);
}
